import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC
} from "react";
import { toast } from "sonner";

import {
  toCalendarEvents,
  type CalendarEvent
} from "@/features/calendar/calendarModel";
import { AgendaView } from "@/features/calendar/components/AgendaView";
import { CalendarToolbar } from "@/features/calendar/components/CalendarToolbar";
import { EventSheet } from "@/features/calendar/components/EventSheet";
import { MonthView } from "@/features/calendar/components/MonthView";
import { TimeGridView } from "@/features/calendar/components/TimeGridView";
import {
  DEFAULT_REMINDER_FILTERS,
  draftForSlot,
  emptyReminderDraft,
  filterReminders,
  reminderDraftSchema,
  reminderDraftToPayload,
  reminderFocusDomId,
  reminderToDraft,
  sortRemindersByDue,
  type ReminderDraft,
  type ReminderFilters,
  type ReminderPayload
} from "@/features/calendar/remindersModel";
import { useCalendarState } from "@/features/calendar/useCalendarState";
import { ForeignNationalsAPI } from "../../../../api/foreignNationals";
import type {
  ForeignNationalReminder,
  ReminderStatus
} from "../../../../api/types/foreignNationalReminder";
import { usePracticeTeamMembers } from "../../../../hooks/usePracticeTeamMembers";
import { useUserRoleAndSubscription } from "../../../../hooks/useUserRoleAndSubscription";
import { useUserProfiles } from "../../../userProfiles/context/UserProfilesContext";
import { PanelError, PanelLoading } from "../../components/PanelState";
import { SectionHeader } from "../../components/SectionHeader";
import { useClientProfile } from "../../context/ClientProfileContext";
import { useFocusScroll } from "../../hooks/useFocusScroll";
import { useRecordCrud } from "../../hooks/useRecordCrud";

const getId = (r: ForeignNationalReminder) => r.reminder_id;

/**
 * Appointments & Reminders: a month / week / day / agenda calendar over the
 * client's reminders, with a side-panel editor for timed appointments and
 * all-day reminders.
 */
export const RemindersTab: FC = () => {
  const { clientId, client, url, badges } = useClientProfile();
  const { lawyerPractice } = useUserRoleAndSubscription();
  const { options: teamMemberOptions } = usePracticeTeamMembers(lawyerPractice);
  const teamOptions = useMemo(
    () => teamMemberOptions.map((o) => ({ value: o.uid, label: o.label })),
    [teamMemberOptions]
  );
  const { ensure: ensureProfiles } = useUserProfiles();

  const [filters, setFilters] = useState<ReminderFilters>(
    DEFAULT_REMINDER_FILTERS
  );
  const [isRecomputing, setIsRecomputing] = useState(false);
  const [isStatusBusy, setIsStatusBusy] = useState(false);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => setFilters(DEFAULT_REMINDER_FILTERS), [clientId]);

  // Keep "today" and the current-time line fresh without re-rendering often.
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(timer);
  }, []);

  const calendar = useCalendarState();

  const crud = useRecordCrud<
    ForeignNationalReminder,
    ReminderDraft,
    ReminderPayload
  >({
    clientId,
    noun: "Entry",
    list: ForeignNationalsAPI.listReminders,
    create: ForeignNationalsAPI.createReminder,
    update: ForeignNationalsAPI.updateReminder,
    remove: ForeignNationalsAPI.deleteReminder,
    getId,
    toDraft: reminderToDraft,
    emptyDraft: emptyReminderDraft,
    toPayload: reminderDraftToPayload,
    schema: reminderDraftSchema,
    sort: sortRemindersByDue,
    afterMutate: async () => badges.refresh(),
    // Profile expirations and dependents regenerate system reminders too.
    invalidateOn: ["reminders", "client", "records"]
  });

  useEffect(() => {
    const ids = new Set<string>();
    crud.records.forEach((r) =>
      (r.assigned_to_user_ids ?? []).forEach((id) => id && ids.add(id))
    );
    if (ids.size) ensureProfiles(Array.from(ids));
  }, [crud.records, ensureProfiles]);

  const visible = useMemo(
    () => filterReminders(crud.records, filters),
    [crud.records, filters]
  );
  const events = useMemo(() => toCalendarEvents(visible), [visible]);

  // Deep link: bring the focused entry's date into view, then highlight it.
  const focusId = url.state.focus.reminderId ?? null;
  const focusedRecord = useMemo(
    () =>
      focusId ? crud.records.find((r) => getId(r) === focusId) : undefined,
    [crud.records, focusId]
  );
  const anchoredFocusRef = useRef<string | null>(null);
  useEffect(() => {
    if (
      !focusedRecord ||
      anchoredFocusRef.current === focusedRecord.reminder_id
    )
      return;
    const [event] = toCalendarEvents([focusedRecord]);
    if (!event) return;
    anchoredFocusRef.current = focusedRecord.reminder_id;
    calendar.setAnchor(event.start);
  }, [calendar, focusedRecord]);
  const { highlightedId } = useFocusScroll(
    focusId ? reminderFocusDomId(focusId) : null,
    !crud.isLoading && Boolean(focusedRecord)
  );

  const defaultAssignees = useMemo(
    () => client?.assigned_to_user_ids ?? [],
    [client?.assigned_to_user_ids]
  );

  const openNew = useCallback(
    () =>
      crud.openCreate(
        draftForSlot(calendar.anchor, false, {
          assignedToUserIds: defaultAssignees
        })
      ),
    [calendar.anchor, crud, defaultAssignees]
  );

  const openAt = useCallback(
    (start: Date, allDay: boolean) =>
      crud.openCreate(
        draftForSlot(start, allDay, { assignedToUserIds: defaultAssignees })
      ),
    [crud, defaultAssignees]
  );

  const openEvent = useCallback(
    (event: CalendarEvent) => {
      const record = crud.records.find(
        (r) => getId(r) === event.reminder?.reminder_id
      );
      if (record) crud.openEdit(record);
    },
    [crud]
  );

  const editingId = crud.editor.mode === "edit" ? crud.editor.id : null;
  const editingRecord = editingId
    ? crud.records.find((r) => getId(r) === editingId) ?? null
    : null;

  const setStatus = useCallback(
    async (status: ReminderStatus) => {
      if (!editingRecord) return;
      setIsStatusBusy(true);
      try {
        await ForeignNationalsAPI.updateReminder(
          clientId,
          editingRecord.reminder_id,
          { status }
        );
        toast.success(
          status === "COMPLETED"
            ? "Marked complete"
            : status === "DISMISSED"
            ? "Dismissed"
            : "Re-opened"
        );
        await crud.refresh();
        badges.refresh();
        crud.close();
      } catch (e) {
        toast.error("Couldn't update", {
          description: e instanceof Error ? e.message : undefined
        });
      } finally {
        setIsStatusBusy(false);
      }
    },
    [badges, clientId, crud, editingRecord]
  );

  const recompute = useCallback(async () => {
    if (isRecomputing) return;
    setIsRecomputing(true);
    try {
      await ForeignNationalsAPI.recomputeReminders(clientId);
      await crud.refresh();
      badges.refresh();
      toast.success("Reminders refreshed", {
        description:
          "Automatic reminders were recomputed from the client's dates."
      });
    } catch (e) {
      toast.error("Couldn't refresh reminders", {
        description: e instanceof Error ? e.message : undefined
      });
    } finally {
      setIsRecomputing(false);
    }
  }, [badges, clientId, crud, isRecomputing]);

  const openCount = crud.records.filter((r) => r.status === "OPEN").length;

  const renderView = () => {
    if (crud.error && crud.records.length === 0) {
      return (
        <PanelError
          title="Couldn't Load Appointments & Reminders"
          message={crud.error}
          onRetry={() => {
            crud.refresh().catch(() => undefined);
          }}
        />
      );
    }
    if (crud.isLoading && crud.records.length === 0) {
      return <PanelLoading text="Loading calendar…" />;
    }
    switch (calendar.view) {
      case "week":
      case "day":
        return (
          <TimeGridView
            mode={calendar.view}
            anchor={calendar.anchor}
            events={events}
            highlightedId={highlightedId}
            onOpenEvent={openEvent}
            onCreateAt={openAt}
            now={now}
          />
        );
      case "agenda":
        return (
          <AgendaView
            anchor={calendar.anchor}
            events={events}
            highlightedId={highlightedId}
            onOpenEvent={openEvent}
            onCreate={openNew}
            now={now}
          />
        );
      case "month":
      default:
        return (
          <MonthView
            anchor={calendar.anchor}
            events={events}
            highlightedId={highlightedId}
            onOpenEvent={openEvent}
            onCreateAt={openAt}
            now={now}
          />
        );
    }
  };

  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      <SectionHeader
        title="Appointments & Reminders"
        description={openCount ? `${openCount} open` : undefined}
      />

      <CalendarToolbar
        view={calendar.view}
        anchor={calendar.anchor}
        onView={calendar.setView}
        onToday={calendar.today}
        onPrev={calendar.prev}
        onNext={calendar.next}
        filters={filters}
        onFilters={setFilters}
        teamOptions={teamOptions}
        onNew={openNew}
        onRecompute={() => {
          recompute().catch(() => undefined);
        }}
        isRecomputing={isRecomputing}
      />

      <div className="min-h-0 flex-1 bg-surface">{renderView()}</div>

      <EventSheet
        open={crud.editor.mode !== "closed"}
        onOpenChange={(open) => {
          if (!open) crud.close();
        }}
        form={crud.form}
        onSubmit={crud.submit}
        record={editingRecord}
        isSaving={crud.isSaving}
        isDeleting={crud.isDeleting}
        onDelete={() => {
          crud.deleteCurrent().catch(() => undefined);
        }}
        onSetStatus={(status) => {
          setStatus(status).catch(() => undefined);
        }}
        isStatusBusy={isStatusBusy}
      />
    </div>
  );
};
